interface StatusBadgeProps {
    validationStatus: string;
    analysisStatus: string;
    complianceStatus?: string | null;
    size?: 'sm' | 'md';
}

// Calm, enterprise status palette: only meaningful states carry colour, in a
// muted green / amber / red / neutral set (no decorative blue or orange).
// "In progress" states read as neutral, not a bright accent.
const NEUTRAL = { bg: 'bg-gray-100 dark:bg-gray-700/60', text: 'text-gray-600 dark:text-gray-300' };
const OK = { bg: 'bg-green-50 dark:bg-green-900/20', text: 'text-green-700 dark:text-green-300' };
const WARN = { bg: 'bg-amber-50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300' };
const BAD = { bg: 'bg-red-50 dark:bg-red-900/20', text: 'text-red-700 dark:text-red-300' };

const VALIDATION_STYLES: Record<string, { bg: string; text: string; label: string }> = {
    none: { ...NEUTRAL, label: 'Sin validar' },
    valid: { ...OK, label: 'Validado' },
    has_issues: { ...WARN, label: 'Con observaciones' },
    error: { ...BAD, label: 'Error validación' },
};

const ANALYSIS_STYLES: Record<string, { bg: string; text: string; label: string }> = {
    none: { ...NEUTRAL, label: 'Sin análisis' },
    pending: { ...NEUTRAL, label: 'Pendiente' },
    processing: { ...NEUTRAL, label: 'Procesando' },
    completed: { ...OK, label: 'Completado' },
    failed: { ...BAD, label: 'Fallido' },
};

const COMPLIANCE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
    compliant: { ...OK, label: 'Compliant' },
    non_compliant: { ...BAD, label: 'Non-Compliant' },
    inconclusive: { ...WARN, label: 'Inconcluso' },
    ready_for_review: { ...NEUTRAL, label: 'Para revisión' },
    needs_manual_review: { ...WARN, label: 'Revisión manual' },
    insufficient_data: { ...NEUTRAL, label: 'Datos insuficientes' },
};

function Badge({ style, size }: { style: { bg: string; text: string; label: string }; size: 'sm' | 'md' }) {
    const sizeClasses = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-xs px-2.5 py-1';
    return (
        <span className={`inline-flex items-center rounded-full font-medium ${style.bg} ${style.text} ${sizeClasses}`}>
            {style.label}
        </span>
    );
}

export default function StatusBadge({ validationStatus, analysisStatus, complianceStatus, size = 'sm' }: StatusBadgeProps) {
    // Show the most relevant badge based on workflow state
    // Priority: compliance > analysis > validation
    if (complianceStatus && COMPLIANCE_STYLES[complianceStatus]) {
        return <Badge style={COMPLIANCE_STYLES[complianceStatus]} size={size} />;
    }

    if (analysisStatus !== 'none' && ANALYSIS_STYLES[analysisStatus]) {
        return <Badge style={ANALYSIS_STYLES[analysisStatus]} size={size} />;
    }

    const valStyle = VALIDATION_STYLES[validationStatus] || VALIDATION_STYLES.none;
    return <Badge style={valStyle} size={size} />;
}

export function ValidationBadge({ status, size = 'sm' }: { status: string; size?: 'sm' | 'md' }) {
    const style = VALIDATION_STYLES[status] || VALIDATION_STYLES.none;
    return <Badge style={style} size={size} />;
}

export function AnalysisBadge({ status, size = 'sm' }: { status: string; size?: 'sm' | 'md' }) {
    const style = ANALYSIS_STYLES[status] || ANALYSIS_STYLES.none;
    return <Badge style={style} size={size} />;
}
